import React, {useEffect, useRef, useState} from 'react';
import styles from './popup-menu.module.scss';
import Popper from '@mui/material/Popper';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import {v4 as uuidv4} from 'uuid';
import {PopupMenuAction, PopupMenuSize} from './types';
import classNames from 'classnames';
import Button from '@mui/material/Button';

interface Props {
    actionIcon: React.ReactElement,
    actions: Array<PopupMenuAction>,
    size?: PopupMenuSize,
    rowItem?: any;
    newGen?: boolean;
    disablePortal?: boolean;
    disabled?: boolean;
}

export default function PopupMenuActions(props: Props) {
    const {actions, rowItem, size, disabled} = props;
    const [open, setOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const handleToggle = () => {
        if (disabled) {
            return;
        }
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = () => {
        setOpen(false);
    };

    function handleListKeyDown(event: React.KeyboardEvent) {
        if (event.key === 'Tab') {
            setOpen(false);
        } else if (event.key === 'Escape') {
            setOpen(false);
        }
    }

    const prevOpen = React.useRef(open);
    useEffect(() => {
        if (prevOpen.current && !open) {
            buttonRef.current?.focus();
        }

        prevOpen.current = open;
    }, [open]);

    return (
        <div
            id="composition-button"
            aria-controls={open ? 'composition-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="true"
            className={classNames({
                [styles.menu]: true,
                [styles.active]: open,
                [styles.small]: size ? size == 'small' : false,
            })}
        >
            <Button
                ref={buttonRef}
                onClick={handleToggle}
                disabled={disabled}
                className={classNames({
                    [styles.actionIcon]: size === undefined || size !== 'small',
                    [styles.smallActionIcon]: size && size === 'small',
                    [styles.active]: open,
                    [styles.round]: props.newGen === undefined || !props.newGen
                })}>
                {props.actionIcon}
            </Button>
            <Popper
                disablePortal={props.disablePortal}
                open={open}
                anchorEl={buttonRef.current}
                role={undefined}
                placement={'bottom-start'}
                className={styles.popper}
                transition
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        className={classNames(
                            styles.popperGrow,
                            {
                                [styles.popperGrowBottomStart]: placement === 'bottom-start',
                            }
                        )}
                    >
                        <Paper
                            sx={{ bgcolor: 'var(--background-secondary-not-transparent)', borderRadius: '8px' }}>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MenuList
                                    autoFocusItem={open}
                                    id="composition-menu"
                                    aria-labelledby="composition-button"
                                    onKeyDown={handleListKeyDown}
                                    className={styles.actionMenu}>
                                    {actions?.map((action: PopupMenuAction) => {
                                        return (
                                            <MenuItem key={uuidv4()} onClick={(e) => {
                                                handleClose()
                                                return action.callback(rowItem);
                                            }} className={styles.actionItem} disabled={action.disabled}>
                                                {action.icon}
                                                <span className={styles.iconText}>{action.name}</span>
                                            </MenuItem>)
                                    })}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </div>
    )
}
